import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { ExtensionType, CallPlan, Feature, TenantTrunk } from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export const productKeys = {
  features: () => ['features'] as const,
  extensionTypes: () => ['extension-types'] as const,
  callPlans: () => ['call-plans'] as const,
  tenantTrunks: (tenantId: string) => ['tenant-trunks', tenantId] as const,
};

export const useFeatures = () =>
  useQuery({
    queryKey: productKeys.features(),
    queryFn: () => api.get<ListResponse<Feature>>('/v1/features'),
    // The feature catalogue changes on deploys, not on user actions.
    staleTime: 10 * 60_000,
  });

export const useExtensionTypes = () =>
  useQuery({
    queryKey: productKeys.extensionTypes(),
    queryFn: () => api.get<ListResponse<ExtensionType>>('/v1/extension-types'),
  });

export function useSaveExtensionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/extension-types/${id}`, body)
         : api.post('/v1/extension-types', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.extensionTypes() }); },
  });
}

export function useDeleteExtensionType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/extension-types/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.extensionTypes() }); },
  });
}

export const useCallPlans = () =>
  useQuery({
    queryKey: productKeys.callPlans(),
    queryFn: () => api.get<ListResponse<CallPlan>>('/v1/call-plans'),
  });

export function useSaveCallPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/v1/call-plans', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.callPlans() }); },
  });
}

export const useTenantTrunks = (tenantId: string | null) =>
  useQuery({
    queryKey: productKeys.tenantTrunks(tenantId ?? ''),
    queryFn: () => api.get<ListResponse<TenantTrunk>>(`/v1/tenants/${tenantId}/trunks`),
    enabled: !!tenantId,
  });

export function useLinkTrunk(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/v1/tenants/${tenantId}/trunks`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.tenantTrunks(tenantId) }); },
  });
}

export function useUnlinkTrunk(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/trunks/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: productKeys.tenantTrunks(tenantId) }); },
  });
}
